import type { IGroup, IRule, IScope } from '@/types/filterBuilderTypes';
import { isGroup, isRule } from '@/types/typeguards/QueryBuilder';
import type { QuickSearchSelection } from '@/utils/quickSearchFields';

/**
 * The list's rounded search box adds ONE ordinary rule to the list's filter, under a
 * fixed id. Because it is an ordinary rule, export, bulk actions and saved views all see
 * exactly the rows on screen. It is not shown as a chip and is not counted on the
 * funnel, because the search box already shows it.
 *
 * Since SAU-467 it is a quick search: the backend looks for the text in the record
 * type's quick search fields. The rule kept in the filter, and so in a saved view, holds
 * only the text. The person's selected fields and Matches all / Matches any are added
 * when a request is built ({@link withQuickSearchSelection}), so opening or saving a view
 * never changes them.
 */
export const LIST_SEARCH_RULE_ID = 'list-search';

/** The criterion, and the stand-in column, the backend reads as a quick search. */
export const QUICK_SEARCH_CRITERION = 'quick_search';

/** A list-search rule as the request carries it, with the person's selection added. */
export interface QuickSearchRule extends IRule {
    quick_fields?: string[];
    quick_mode?: QuickSearchSelection['mode'];
}

/** The rule for one search: the text only. */
export function listSearchRule(text: string): IRule {
    return { id: LIST_SEARCH_RULE_ID, column: QUICK_SEARCH_CRITERION, field: null, criterion: QUICK_SEARCH_CRITERION, value: text };
}

/** Every leaf rule in the filter, in order. */
export function filterLeafRules(scope: IScope | null | undefined): IRule[] {
    if (!scope) {
        return [];
    }
    if (isRule(scope)) {
        return [scope];
    }
    return isGroup(scope) ? scope.rules.flatMap((child) => filterLeafRules(child)) : [];
}

/** The filter with one rule taken out, wherever it sits. Empty groups are dropped. */
export function withoutRule(scope: IScope, ruleId: string): IGroup {
    const root: IGroup = isGroup(scope) ? scope : { id: 'root', scopeType: 'and', rules: [scope] };
    const prune = (group: IGroup): IGroup => ({
        ...group,
        rules: group.rules
            .filter((child) => !(isRule(child) && child.id === ruleId))
            .map((child) => (isGroup(child) ? prune(child) : child))
            .filter((child) => !(isGroup(child) && child.rules.length === 0)),
    });
    return prune(root);
}

/**
 * The filter with the list-search rule set to `rule`, or removed when `rule` is
 * null. The rule always sits at the top level beside the user's own rules, which
 * are ANDed, so the search narrows whatever the filter already shows.
 */
export function withListSearch(scope: IScope | null | undefined, rule: IRule | null): IGroup {
    const base: IGroup = scope ? withoutRule(scope, LIST_SEARCH_RULE_ID) : { id: 'root', scopeType: 'and', rules: [] };
    const root: IGroup = base.scopeType === 'and' ? base : { id: 'root', scopeType: 'and', rules: [base] };
    return rule ? { ...root, rules: [...root.rules, rule] } : root;
}

/**
 * What the search box should show for this filter. A rule from before SAU-467, such as
 * `{column: 'last_name', criterion: 'contains'}` in an old saved view, still gives its text.
 */
export function listSearchText(scope: IScope | null | undefined): string {
    const rule = filterLeafRules(scope).find((candidate) => candidate.id === LIST_SEARCH_RULE_ID);
    return typeof rule?.value === 'string' ? rule.value : '';
}

/**
 * The filter as a request sends it: the list-search rule, in whatever shape it was saved,
 * becomes a quick search of its text with the person's fields and mode. A view saved
 * before SAU-467 searched one column, and its column is dropped, so its text is searched
 * on the person's current fields. Every other rule is left as it is.
 */
export function withQuickSearchSelection(scope: IScope | null | undefined, selection: QuickSearchSelection): IScope | null | undefined {
    const text = listSearchText(scope);
    if (!scope || !filterLeafRules(scope).some((rule) => rule.id === LIST_SEARCH_RULE_ID)) {
        return scope;
    }
    if (text.trim() === '') {
        return withListSearch(scope, null);
    }
    const rule: QuickSearchRule = { ...listSearchRule(text), quick_fields: [...selection.fields], quick_mode: selection.mode };
    return withListSearch(scope, rule);
}
